/**
 * laptops-phone-validation.js — the site's shared phone module. Two jobs:
 *
 *   LG_phoneValid(raw)  the single VALIDATION rule for BOTH lead forms — the cart
 *                       (≈90% of revenue) and the product-page call-me form.
 *   LG_phone(raw)       E.164 normalization for AD PLATFORMS only (Meta Pixel + CAPI).
 *
 * ⚠ CHANGING LG_phoneValid CHANGES WHO CAN BUY. Both lead forms gate on it. Never use
 * LG_phone for validation: it is stricter and would turn away customers the site
 * accepts today.
 *
 * WHY THIS EXISTS
 * The browser Pixel and the server CAPI used to receive the phone in two
 * different shapes, so Meta hashed two different strings for the same person
 * and the phone never contributed to matching. Both channels must be fed the
 * SAME E.164 digit string; Meta's own client-side normalization is then a
 * no-op and its hash equals the server's sha256 of the identical string.
 *
 * CONTRACT
 *   LG_phoneValid(raw) -> true   let this lead through
 *                      -> false  reject it, exactly as the site rejects it today
 *                      -> null   "could not decide" — the CALLER MUST fall back to its
 *                                own inline copy of the rule. Callers test `=== null`
 *                                strictly, so this must never return another falsy value.
 *   Semantically — not textually — identical to the two inline rules it replaced: the
 *   cart wrote it in rejection form (`!A || B`) and the call-me form lowercased first.
 *   Both were proven equivalent by a differential run; repeat that run before touching it.
 *
 *   LG_phone(raw) -> 'digits'  the resolved E.164 number, no '+', no spaces
 *                 -> ''        could not be resolved — the CALLER MUST OMIT
 *                              the phone parameter entirely (do not send '')
 *   Always returns a string and never throws, so a caller may chain string
 *   methods on the result without guarding. Exotic input that cannot even be
 *   stringified (Symbol, an object with a throwing toString) yields ''.
 *
 * NOT FOR THE ORDER PATH. The CRM / bank payload keeps sending the raw user
 * input untouched — operators read those numbers.
 *
 * Georgian numbering plan (ComCom / ITU) — 9-digit national significant number:
 *   5 = mobile (all operators)
 *   3 = eastern geographic (Tbilisi = 32)
 *   4 = western geographic (Batumi = 422, Kutaisi = 431, ...)
 *   0/1/2/6 = trunk prefix / short codes / reserved
 *   7 = non-geographic, 8 = toll-free, 9 = premium rate  (never a customer number)
 */
(function (w) {
  var GE_CC = '995';
  var GE_FIRST_DIGIT = { '3': true, '4': true, '5': true };

  /**
   * The site's single phone VALIDATION rule — "may this lead through?".
   *
   * Byte-identical to the rule that was written out by hand, twice, in
   * custom-cart-laptops.liquid and laptops-call-me-form.liquid. Moved here so it
   * exists in one place; behaviour is unchanged on purpose, proven by a
   * differential run over the old and new rule.
   *
   * Deliberately permissive (7-15 digits, any country, no country-code
   * requirement). Tightening it would reject customers the site accepts today —
   * a business decision, not a code one. Do NOT use LG_phone() here: it is
   * stricter and would silently start turning real customers away.
   *
   * Callers must keep their own copy of the rule as a fallback, so a missing
   * asset can never open the gate.
   */
  w.LG_phoneValid = function (raw) {
    try {
      var v = String(raw == null ? '' : raw).replace(/[\s\-().]/g, '');
      return /^\+?\d{7,15}$/.test(v) && !/^\+?(\d)\1+$/.test(v);
    } catch (e) {
      return null; // "I could not decide" — the caller falls back to its own rule
    }
  };

  w.LG_phone = function (raw) {
    try {
      if (raw === null || raw === undefined) return '';

      var d = String(raw).replace(/\D/g, '');
      if (!d) return '';

      // International access prefix, e.g. 00995...
      if (d.indexOf('00') === 0) d = d.slice(2);

      // Domestic trunk prefix, e.g. 0322 12 34 56 (never part of E.164)
      while (d.charAt(0) === '0') d = d.slice(1);

      // Already a complete Georgian number
      if (d.indexOf(GE_CC) === 0 && d.length === 12) return d;

      // Bare 9-digit national number: Georgian only if the range says so
      if (d.length === 9) return GE_FIRST_DIGIT[d.charAt(0)] ? GE_CC + d : '';

      // Longer than 9 digits: a country code is already present, leave it alone
      if (d.length > 9) return d;

      // Shorter than 9 digits: not identifiable
      return '';
    } catch (e) {
      // Only String(raw) above can throw, so raw is not stringifiable — nothing usable
      // to send. '' keeps the documented contract: always a string, caller omits.
      return '';
    }
  };
})(window);
